import { useState } from 'react';
import MetricCard from './components/MetricCard.jsx';
import DevModeBanner from './components/DevModeBanner.jsx';
import { sanitizeCandidateRawData } from '../machine.js';

const METRIC_DEFINITIONS = [
  {
    key: 'capacityDownloadMbps',
    label: 'Internet download capacity',
    unit: 'Mbps',
    format: v => v.toFixed(1),
    threshold: 'Primary download read from Cloudflare browser speed-test endpoints.'
  },
  {
    key: 'capacityUploadMbps',
    label: 'Internet upload capacity',
    unit: 'Mbps',
    format: v => v.toFixed(1),
    threshold: 'Primary upload read from Cloudflare browser speed-test endpoints.'
  },
  {
    key: 'downloadMbps',
    timeoutKey: 'downloadTimedOut',
    rawKey: 'download',
    label: 'Single-stream Convertros download',
    unit: 'Mbps',
    format: v => v.toFixed(1),
    threshold: 'Reference signal: one timed browser stream to Convertros infrastructure.'
  },
  {
    key: 'uploadMbps',
    timeoutKey: 'uploadTimedOut',
    rawKey: 'upload',
    label: 'Single-stream Convertros upload',
    unit: 'Mbps',
    format: v => v.toFixed(1),
    threshold: 'Reference signal: one browser stream to Convertros infrastructure.'
  },
  {
    key: 'packetLossPct',
    label: 'Packet loss',
    unit: '%',
    format: v => v.toFixed(2),
    threshold: 'Lower is better for clear voice quality.'
  },
  {
    key: 'jitterMs',
    label: 'Jitter',
    unit: 'ms',
    format: v => v.toFixed(1),
    threshold: 'Lower is better for steady voice audio.'
  },
  {
    key: 'rttMs',
    label: 'Round-trip latency',
    unit: 'ms',
    format: v => v.toFixed(0),
    threshold: 'Lower is better for natural conversation timing.'
  },
  {
    key: 'bufferbloatIncreaseMs',
    label: 'Bufferbloat (latency increase under load)',
    unit: 'ms',
    format: v => v.toFixed(0),
    threshold: 'Shows how much latency rises when the connection is busy.'
  },
  {
    key: 'websocketP95Ms',
    label: 'WebSocket p95',
    unit: 'ms',
    format: v => v.toFixed(0),
    threshold: 'Lower is better for real-time lead notification.'
  },
  {
    key: 'mosScore',
    label: 'Voice quality (MOS)',
    unit: '',
    format: v => v.toFixed(2),
    threshold: 'Higher is better for estimated voice quality.'
  }
];

const DEFAULT_TRANSFER_BYTES = {
  download: 50 * 1024 * 1024,
  upload: 10 * 1024 * 1024
};

const DEFAULT_TRANSFER_LABELS = {
  download: '50.0 MB',
  upload: '10.0 MB'
};

function formatTransferredMb(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n)) return null;
  return `${(n / 1_000_000).toFixed(1)} MB`;
}

function formatTargetMb(bytes, direction) {
  const n = Number(bytes);
  if (!Number.isFinite(n)) return null;
  if (n === DEFAULT_TRANSFER_BYTES[direction]) return DEFAULT_TRANSFER_LABELS[direction];
  return `${(n / 1_000_000).toFixed(1)} MB`;
}

function formatMbps(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return `${n.toFixed(1)} Mbps`;
}

function formatSeconds(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n)) return null;
  return `${(n / 1000).toFixed(1)}s`;
}

function buildMetricCardProps(result, def) {
  const value = result.metrics[def.key];
  const verdict = result.verdicts[def.key];
  const base = {
    label: def.label,
    value: value !== undefined && value !== null ? def.format(value) : '--',
    unit: def.unit,
    verdict,
    thresholdHint: def.threshold
  };

  if (!def.rawKey) return base;

  const raw = result.raw_metrics?.bandwidth?.[def.rawKey] || {};
  const timedOut = result.metrics[def.timeoutKey] === true || raw.timedOut === true;
  if (!timedOut) {
    if (raw.sampled === true) {
      const transferred = formatTransferredMb(raw.bytes);
      const duration = formatSeconds(raw.durationMs);
      const action = def.rawKey === 'download' ? 'received' : 'sent';
      return {
        ...base,
        detail: duration && transferred
          ? `Timed ${duration} path sample; ${transferred} ${action}.`
          : 'Timed path sample.'
      };
    }
    return base;
  }

  const transferred = formatTransferredMb(raw.bytes);
  const target = formatTargetMb(raw.requestedBytes || raw.totalBytes || DEFAULT_TRANSFER_BYTES[def.rawKey], def.rawKey);
  const partial = formatMbps(value ?? raw.mbps);
  const action = def.rawKey === 'download' ? 'received' : 'sent';
  const transferText = transferred && target
    ? `${transferred} of ${target} ${action} before timeout`
    : transferred
      ? `${transferred} ${action} before timeout`
      : `Transfer timed out before enough data was ${action}`;

  return {
    ...base,
    statusText: 'Incomplete',
    detail: partial
      ? `${transferText}. Partial throughput: ${partial}.`
      : transferText
  };
}

function hasVerdict(result, keys, verdicts) {
  return keys.some(key => verdicts.includes(result.verdicts?.[key]));
}

function buildCandidateInsights(result) {
  const insights = [];
  const speedKeys = ['capacityDownloadMbps', 'capacityUploadMbps'];
  const voiceKeys = ['packetLossPct', 'jitterMs', 'rttMs', 'mosScore'];
  const busyConnectionKeys = ['bufferbloatIncreaseMs'];
  const realTimeKeys = ['websocketP95Ms'];

  if (hasVerdict(result, speedKeys, ['fail', 'borderline'])) {
    insights.push('Your capacity speed was lower than expected. A wired connection, closing streaming or cloud-sync apps, and retesting from your normal work setup can help isolate whether this was temporary.');
  }

  if (hasVerdict(result, voiceKeys, ['fail', 'borderline'])) {
    insights.push('Voice-quality signals showed some risk. If you were on WiFi, try moving closer to the router or using ethernet before retesting.');
  }

  if (hasVerdict(result, busyConnectionKeys, ['fail', 'borderline'])) {
    insights.push('Latency rose while the connection was under load. This usually points to other traffic on the network, router congestion, or cloud/video apps running in the background.');
  }

  if (hasVerdict(result, realTimeKeys, ['fail', 'borderline'])) {
    insights.push('Real-time notification latency was higher than expected. A retest from the same setup with other apps closed can show whether this was a one-time spike.');
  }

  if (hasVerdict(result, Object.keys(result.verdicts || {}), ['no_data', 'inconclusive'])) {
    insights.push('One or more measurements could not complete. Chrome on your normal home network, with VPNs disabled where possible, gives the cleanest read.');
  }

  if (!insights.length) {
    insights.push('Your connection and browser signals look steady from this run. Recruiting will use the saved scores as a baseline.');
  }

  return insights;
}

const MACHINE_METRIC_DEFINITIONS = [
  {
    key: 'browserVersion',
    verdictKey: 'browserAgeMonths',
    label: 'Browser version',
    unit: '',
    format: v => v,
    threshold: 'Current desktop browser is recommended.'
  },
  {
    key: 'cpuOpsPerSec',
    verdictKey: 'cpuOpsPerSec',
    label: 'CPU throughput',
    unit: 'M ops/s',
    format: v => (v / 1000000).toFixed(1),
    threshold: 'Higher is better for keeping browser work responsive.'
  },
  {
    key: 'deviceMemoryGb',
    verdictKey: 'deviceMemoryGb',
    label: 'Device memory',
    unit: 'GB',
    format: v => Number(v).toFixed(0),
    threshold: 'More memory helps with browser stability.'
  },
  {
    key: 'cpuCores',
    verdictKey: 'cpuCores',
    label: 'CPU cores',
    unit: '',
    format: v => String(v),
    threshold: 'More cores help with multitasking.'
  },
  {
    key: 'hardwareAccelerationEnabled',
    verdictKey: 'hardwareAccelerationEnabled',
    label: 'Hardware acceleration',
    unit: '',
    format: v => v ? 'Enabled' : 'Off',
    threshold: 'Recommended for smoother browser performance.'
  },
  {
    key: 'webrtcSupported',
    verdictKey: 'webrtcSupported',
    label: 'WebRTC voice support',
    unit: '',
    format: v => v ? 'Supported' : 'Unsupported',
    threshold: 'Needed for browser-based voice tools.'
  },
  {
    key: 'webgl2Supported',
    verdictKey: 'webgl2Supported',
    label: 'WebGL2 support',
    unit: '',
    format: v => v ? 'Supported' : 'Unsupported',
    threshold: 'Helps confirm current browser graphics support.'
  }
];

function SaveStatusBanner({ saveStatus, saveError, onRetrySave }) {
  if (saveStatus === 'idle' || saveStatus === 'disabled') return null;

  if (saveStatus === 'saving') {
    return (
      <div className="rounded-md bg-slate-50 border border-slate-200 px-4 py-2 text-sm text-slate-700">
        Saving result...
      </div>
    );
  }

  if (saveStatus === 'saved') {
    return (
      <div className="rounded-md bg-emerald-50 border border-emerald-200 px-4 py-2 text-sm text-emerald-900">
        Result saved. Your recruiter will see it.
      </div>
    );
  }

  if (saveStatus === 'failed') {
    return (
      <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-900 space-y-2">
        <p>
          <strong className="font-semibold">Could not save the result.</strong>
          {' '}Your recruiter will not see this run unless you retry. The test itself completed correctly; the metrics below are accurate.
        </p>
        {saveError ? (
          <p className="break-words text-xs text-red-800">{saveError}</p>
        ) : null}
        <button
          type="button"
          onClick={onRetrySave}
          className="inline-flex items-center rounded-md bg-red-900 px-3 py-1.5 text-sm font-medium text-white shadow hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-red-500"
        >
          Retry save
        </button>
      </div>
    );
  }

  return null;
}

export default function ResultsScreen({
  result,
  saveStatus = 'idle',
  saveError = null,
  onRetrySave = () => {}
}) {
  const [showRaw, setShowRaw] = useState(false);
  const insights = buildCandidateInsights(result);

  return (
    <div className="space-y-6">
      <DevModeBanner visible={result.environment?.via_cloudflare_tunnel} />
      <SaveStatusBanner
        saveStatus={saveStatus}
        saveError={saveError}
        onRetrySave={onRetrySave}
      />

      <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Connection scores</h1>
          <p className="mt-1 text-sm text-slate-600">
            Captured {new Date(result.completed_at).toLocaleString()}
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-950">
        <p className="font-semibold">Initial baseline only</p>
        <p className="mt-1">
          These scores are not an employment decision. Recruiting uses them to spot setup issues, understand connection quality, and decide whether a retest or support is needed.
        </p>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-700 shadow-sm">
        <p className="font-semibold text-slate-900">What this means</p>
        <ul className="mt-2 space-y-2">
          {insights.map(item => (
            <li key={item} className="leading-relaxed">{item}</li>
          ))}
        </ul>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {METRIC_DEFINITIONS.map(def => {
          return (
            <MetricCard
              key={def.key}
              {...buildMetricCardProps(result, def)}
            />
          );
        })}
      </div>

      {result.machine ? (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">Machine readiness</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {MACHINE_METRIC_DEFINITIONS.map(def => {
              const value = result.machine[def.key];
              const verdict = result.machine.verdicts?.[def.verdictKey];
              return (
                <MetricCard
                  key={def.key}
                  label={def.label}
                  value={value !== undefined && value !== null ? def.format(value) : '--'}
                  unit={def.unit}
                  verdict={verdict}
                  thresholdHint={def.threshold}
                />
              );
            })}
          </div>
        </section>
      ) : null}

      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <button
          type="button"
          onClick={() => setShowRaw(s => !s)}
          className="text-sm font-medium text-slate-700 hover:underline"
        >
          {showRaw ? 'Hide raw data' : 'Show raw data'}
        </button>
        {showRaw ? (
          <pre className="mt-3 overflow-auto rounded bg-slate-50 p-3 text-xs leading-snug text-slate-800">
            {JSON.stringify(sanitizeCandidateRawData({
              environment: result.environment,
              raw_metrics: result.raw_metrics
            }), null, 2)}
          </pre>
        ) : null}
      </div>
    </div>
  );
}
